import type { Theme } from "@/client/ui/design-system/styles/colors.css";
import { createEl } from "@/client/utils/createEl";
import { pct, px } from "@/client/utils/units";
import {
    rangeSliderContainer,
    rangeSliderFill,
    rangeSliderThumbActive,
    rangeSliderThumbHidden,
    rangeSliderThumbHigh,
    rangeSliderThumbLow,
    rangeSliderTrack,
} from "./range-slider.css";
import { clamp } from "@/shared/utils/clamp";
import { map, mapFrom0to1 } from "@/shared/utils/numberMapping";
import { roundTo } from "@/shared/utils/round";

export interface RangeSliderOptions {
    theme: Theme;
    min?: number;
    max?: number;
    valueLow?: number;
    valueHigh?: number;
    step?: number;
    disabled?: boolean;
    onChange?: (low: number, high: number) => void;
    onInput?: (low: number, high: number) => void;
    ariaLabelLow?: string;
    ariaLabelHigh?: string;
}

export interface RangeSliderComponent {
    el: HTMLDivElement;
    setValues: (low: number, high: number) => void;
    setDisabled: (isDisabled: boolean) => void;
}

const kThumbHalf_px = 8;

const enum ActiveThumb {
    Low = "low",
    High = "high",
}

const clampRange = (
    low: number,
    high: number,
    min: number,
    max: number,
): { low: number; high: number } => {
    const clampedLow = clamp(low, min, max);
    const clampedHigh = clamp(high, min, max);
    return clampedLow > clampedHigh
        ? { low: clampedHigh, high: clampedHigh }
        : { low: clampedLow, high: clampedHigh };
};

export const createRangeSliderComponent = (
    options: RangeSliderOptions,
    init?: (el: HTMLDivElement) => void,
): RangeSliderComponent => {
    const min = options.min ?? 0;
    const max = options.max ?? 100;
    const step = options.step;
    let isDisabled = options.disabled ?? false;

    const initial = clampRange(options.valueLow ?? min, options.valueHigh ?? max, min, max);
    let valueLow = initial.low;
    let valueHigh = initial.high;

    const toPercent = (value: number): number => map(value, min, max, 0, 100, true, true);

    const trackEl = createEl(
        "div",
        rangeSliderTrack({ theme: options.theme, disabled: isDisabled }),
        [],
    );

    const fillEl = createEl(
        "div",
        rangeSliderFill({ theme: options.theme, disabled: isDisabled }),
        [],
    );

    const thumbLowEl = createEl("div", rangeSliderThumbLow, [], (el) => {
        el.tabIndex = 0;
        el.setAttribute("role", "slider");
        el.setAttribute("aria-valuemin", String(min));
        el.setAttribute("aria-valuemax", String(max));
        el.setAttribute("aria-valuenow", String(valueLow));
        if (options.ariaLabelLow !== undefined) {
            el.setAttribute("aria-label", options.ariaLabelLow);
        }
    });

    const thumbHighEl = createEl("div", rangeSliderThumbHigh, [], (el) => {
        el.tabIndex = 0;
        el.setAttribute("role", "slider");
        el.setAttribute("aria-valuemin", String(min));
        el.setAttribute("aria-valuemax", String(max));
        el.setAttribute("aria-valuenow", String(valueHigh));
        if (options.ariaLabelHigh !== undefined) {
            el.setAttribute("aria-label", options.ariaLabelHigh);
        }
    });

    const containerEl = createEl("div", rangeSliderContainer({ disabled: isDisabled }), [
        trackEl,
        fillEl,
        thumbLowEl,
        thumbHighEl,
    ]);

    const updatePositions = (): void => {
        const lowPct = toPercent(valueLow);
        const highPct = toPercent(valueHigh);

        thumbLowEl.style.left = `calc(${pct(lowPct)} - ${px(kThumbHalf_px)})`;
        thumbHighEl.style.left = pct(highPct);
        fillEl.style.left = pct(lowPct);
        fillEl.style.width = pct(highPct - lowPct);

        thumbLowEl.setAttribute("aria-valuenow", String(valueLow));
        thumbHighEl.setAttribute("aria-valuenow", String(valueHigh));
    };

    updatePositions();

    const pointerToRaw = (clientX: number): number => {
        const rect = containerEl.getBoundingClientRect();
        const ratio = clamp((clientX - rect.left) / rect.width, 0, 1);
        return mapFrom0to1(ratio, min, max, true, true);
    };

    const valueFromPointerX =
        step !== undefined
            ? (clientX: number): number => roundTo(pointerToRaw(clientX), step, min)
            : pointerToRaw;

    let activeThumb: ActiveThumb | undefined;

    const applySnapped = (thumb: ActiveThumb, snapped: number): boolean => {
        switch (thumb) {
            case ActiveThumb.Low: {
                const clamped = clamp(snapped, min, valueHigh);
                if (clamped === valueLow) {
                    return false;
                }
                valueLow = clamped;
                return true;
            }
            case ActiveThumb.High: {
                const clamped = clamp(snapped, valueLow, max);
                if (clamped === valueHigh) {
                    return false;
                }
                valueHigh = clamped;
                return true;
            }
        }
    };

    const handleMove = (clientX: number): void => {
        if (activeThumb === undefined) {
            return;
        }

        const snapped = valueFromPointerX(clientX);
        if (applySnapped(activeThumb, snapped)) {
            updatePositions();
            options.onInput?.(valueLow, valueHigh);
        }
    };

    const handlePointerDown = (event: PointerEvent): void => {
        if (isDisabled) {
            return;
        }
        event.preventDefault();
        activeThumb = ((): ActiveThumb => {
            const pointerValue = pointerToRaw(event.clientX);
            const distLow = Math.abs(pointerValue - valueLow);
            const distHigh = Math.abs(pointerValue - valueHigh);
            if (distLow < distHigh) {
                return ActiveThumb.Low;
            }
            if (distHigh < distLow) {
                return ActiveThumb.High;
            }
            return pointerValue <= valueLow ? ActiveThumb.Low : ActiveThumb.High;
        })();
        const activeEl = activeThumb === ActiveThumb.Low ? thumbLowEl : thumbHighEl;
        activeEl.classList.add(rangeSliderThumbActive);
        containerEl.setPointerCapture(event.pointerId);
        handleMove(event.clientX);
    };

    const handlePointerMove = (event: PointerEvent): void => {
        if (activeThumb === undefined) {
            return;
        }
        event.preventDefault();
        handleMove(event.clientX);
    };

    const handlePointerUp = (_event: PointerEvent): void => {
        if (activeThumb === undefined) {
            return;
        }
        const activeEl = activeThumb === ActiveThumb.Low ? thumbLowEl : thumbHighEl;
        activeEl.classList.remove(rangeSliderThumbActive);
        activeThumb = undefined;
        options.onChange?.(valueLow, valueHigh);
    };

    const addThumbHoverListeners = (thumbEl: HTMLDivElement): void => {
        thumbEl.addEventListener("pointerenter", () => {
            if (activeThumb === undefined && !isDisabled) {
                thumbEl.classList.add(rangeSliderThumbActive);
            }
        });
        thumbEl.addEventListener("pointerleave", () => {
            if (activeThumb === undefined) {
                thumbEl.classList.remove(rangeSliderThumbActive);
            }
        });
    };

    addThumbHoverListeners(thumbLowEl);
    addThumbHoverListeners(thumbHighEl);

    containerEl.addEventListener("pointerdown", handlePointerDown);
    containerEl.addEventListener("pointermove", handlePointerMove);
    containerEl.addEventListener("pointerup", handlePointerUp);
    containerEl.addEventListener("pointercancel", handlePointerUp);

    const setValues = (low: number, high: number): void => {
        const clamped = clampRange(low, high, min, max);
        valueLow = clamped.low;
        valueHigh = clamped.high;
        updatePositions();
    };

    const setDisabled = (disabled: boolean): void => {
        isDisabled = disabled;
        containerEl.className = rangeSliderContainer({ disabled: isDisabled });
        trackEl.className = rangeSliderTrack({ theme: options.theme, disabled: isDisabled });
        fillEl.className = rangeSliderFill({ theme: options.theme, disabled: isDisabled });

        if (isDisabled) {
            thumbLowEl.classList.add(rangeSliderThumbHidden);
            thumbHighEl.classList.add(rangeSliderThumbHidden);
            thumbLowEl.removeAttribute("tabindex");
            thumbHighEl.removeAttribute("tabindex");
        } else {
            thumbLowEl.classList.remove(rangeSliderThumbHidden);
            thumbHighEl.classList.remove(rangeSliderThumbHidden);
            thumbLowEl.tabIndex = 0;
            thumbHighEl.tabIndex = 0;
        }
    };

    if (isDisabled) {
        thumbLowEl.classList.add(rangeSliderThumbHidden);
        thumbHighEl.classList.add(rangeSliderThumbHidden);
        thumbLowEl.removeAttribute("tabindex");
        thumbHighEl.removeAttribute("tabindex");
    }

    init?.(containerEl);

    return { el: containerEl, setValues, setDisabled };
};
